import React, { useState } from 'react';
import { authAPI } from '../utils/api';
import { AuthResponse } from '../types';

interface LoginScreenProps {
    onLogin: (user: any, token: string) => void;
    onSwitchToRegister: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin, onSwitchToRegister }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetStep, setResetStep] = useState<'request' | 'verify'>('request');
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response: AuthResponse = await authAPI.login(username, password);

            if (response.success && response.token && response.user) {
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                onLogin(response.user, response.token);
            } else {
                setError(response.error || 'ログインに失敗しました');
            }
        } catch (error: any) {
            setError(error.response?.data?.error || 'ログインに失敗しました');
        } finally {
            setIsLoading(false);
        }
    };

    const openResetModal = () => {
        setShowResetModal(true);
        setResetStep('request');
        setResetEmail('');
        setResetCode('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetError('');
        setResetSuccess('');
        setResetLoading(false);
    };

    const closeResetModal = () => {
        setShowResetModal(false);
        setResetStep('request');
        setResetEmail('');
        setResetCode('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetError('');
        setResetSuccess('');
        setResetLoading(false);
    };

    const handleResetRequest = async (event: React.FormEvent) => {
        event.preventDefault();
        setResetError('');
        setResetSuccess('');

        if (!resetEmail.trim()) {
            setResetError('メールアドレスを入力してください');
            return;
        }

        setResetLoading(true);
        try {
            const sanitizedEmail = resetEmail.trim();
            const response = await authAPI.requestPasswordReset(sanitizedEmail);
            setResetSuccess(
                response.message ||
                    'パスワードリセット手続きをメールに送信しました。メールをご確認ください。'
            );
            setResetEmail(sanitizedEmail);
            setResetStep('verify');
        } catch (error: any) {
            setResetError(
                error.response?.data?.error ||
                    'メールの送信に失敗しました。時間をおいて再度お試しください。'
            );
        } finally {
            setResetLoading(false);
        }
    };

    const handleResetConfirm = async (event: React.FormEvent) => {
        event.preventDefault();
        setResetError('');
        setResetSuccess('');

        if (!resetCode.trim()) {
            setResetError('認証コードを入力してください');
            return;
        }

        if (!resetNewPassword || resetNewPassword !== resetConfirmPassword) {
            setResetError('新しいパスワードが一致しません');
            return;
        }

        setResetLoading(true);
        try {
            const response = await authAPI.resetPassword(
                resetEmail.trim(),
                resetCode.trim(),
                resetNewPassword
            );
            setResetSuccess(
                response.message ||
                    'パスワードをリセットしました。新しいパスワードでログインしてください。'
            );
            setTimeout(() => {
                closeResetModal();
            }, 2000);
        } catch (error: any) {
            setResetError(
                error.response?.data?.error ||
                    'パスワードのリセットに失敗しました。認証コードとパスワードを確認してください。'
            );
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <>
            <style>
                {`
          @media (max-width: 768px) {
            .login-container {
              padding: 20px !important;
            }
            
            .login-card {
              padding: 30px 20px !important;
              margin: 20px !important;
              max-width: 100% !important;
            }
            
            .login-title {
              font-size: 24px !important;
              margin-bottom: 20px !important;
            }
            
            .login-input {
              padding: 12px !important;
              font-size: 16px !important;
              margin-bottom: 15px !important;
            }
            
            .login-button {
              padding: 12px !important;
              font-size: 16px !important;
            }
            
            .login-link {
              font-size: 14px !important;
              margin-top: 15px !important;
            }
          }
          
          @media (max-width: 480px) {
            .login-container {
              padding: 10px !important;
            }
            
            .login-card {
              padding: 20px 15px !important;
              margin: 10px !important;
            }
            
            .login-title {
              font-size: 20px !important;
              margin-bottom: 15px !important;
            }
            
            .login-input {
              padding: 10px !important;
              font-size: 14px !important;
              margin-bottom: 12px !important;
            }
            
            .login-button {
              padding: 10px !important;
              font-size: 14px !important;
            }
            
            .login-link {
              font-size: 12px !important;
              margin-top: 12px !important;
            }
          }
        `}
            </style>
            <div
                className='login-container'
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                    fontFamily: 'Arial, sans-serif',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div
                    className='login-card'
                    style={{
                        background: 'white',
                        padding: '40px',
                        borderRadius: '10px',
                        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
                        width: '100%',
                        maxWidth: '400px',
                        position: 'relative',
                        zIndex: 10,
                    }}
                >
                    <h2
                        className='login-title'
                        style={{
                            textAlign: 'center',
                            marginBottom: '30px',
                            color: '#333',
                            fontSize: '28px',
                        }}
                    >
                        ログイン
                    </h2>

                    {error && (
                        <div
                            style={{
                                background: '#ffebee',
                                color: '#c62828',
                                padding: '10px',
                                borderRadius: '5px',
                                marginBottom: '20px',
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: '20px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '5px',
                                    color: '#555',
                                    fontWeight: 'bold',
                                }}
                            >
                                ユーザー名
                            </label>
                            <input
                                type='text'
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                className='login-input'
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '2px solid #ddd',
                                    borderRadius: '5px',
                                    fontSize: '16px',
                                    boxSizing: 'border-box',
                                }}
                                placeholder='ユーザー名を入力'
                            />
                        </div>

                        <div style={{ marginBottom: '25px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    marginBottom: '5px',
                                }}
                            >
                                <label
                                    style={{
                                        color: '#555',
                                        fontWeight: 'bold',
                                        fontSize: '13px',
                                    }}
                                >
                                    パスワード
                                </label>
                                <button
                                    type='button'
                                    onClick={openResetModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#667eea',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '13px',
                                        padding: 0,
                                    }}
                                >
                                    パスワードを忘れた場合
                                </button>
                            </div>
                            <input
                                type='password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className='login-input'
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '2px solid #ddd',
                                    borderRadius: '5px',
                                    fontSize: '16px',
                                    boxSizing: 'border-box',
                                }}
                                placeholder='パスワードを入力'
                            />
                        </div>

                        <div
                            style={{
                                textAlign: 'right',
                                marginBottom: '25px',
                            }}
                        >
                            <button
                                type='button'
                                onClick={openResetModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#667eea',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontSize: '14px',
                                    padding: 0,
                                }}
                            >
                                パスワードを忘れた場合
                            </button>
                        </div>

                        <button
                            type='submit'
                            disabled={isLoading}
                            className='login-button'
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: isLoading ? '#ccc' : '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background 0.3s',
                            }}
                        >
                            {isLoading ? 'ログイン中...' : 'ログイン'}
                        </button>
                    </form>

                    <div
                        className='login-link'
                        style={{
                            textAlign: 'center',
                            marginTop: '20px',
                            color: '#666',
                        }}
                    >
                        アカウントをお持ちでない方は{' '}
                        <button
                            onClick={onSwitchToRegister}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#667eea',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '16px',
                            }}
                        >
                            新規登録
                        </button>
                    </div>
                </div>
            </div>
            {showResetModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0, 0, 0, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999,
                        padding: '20px',
                    }}
                    onClick={closeResetModal}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            background: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3
                            style={{
                                marginTop: 0,
                                marginBottom: '15px',
                                fontSize: '20px',
                                textAlign: 'center',
                                color: '#333',
                            }}
                        >
                            パスワードの再設定
                        </h3>
                        <p
                            style={{
                                fontSize: '13px',
                                color: '#666',
                                marginBottom: '15px',
                                lineHeight: 1.6,
                                textAlign: 'center',
                            }}
                        >
                            {resetStep === 'request'
                                ? '登録済みのメールアドレスに認証コードを送信します。'
                                : `${resetEmail} に送信された認証コードを入力し、新しいパスワードを設定してください。`}
                        </p>
                        {resetError && (
                            <div
                                style={{
                                    background: 'rgba(255, 235, 59, 0.2)',
                                    color: '#c62828',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px',
                                    fontSize: '12px',
                                }}
                            >
                                {resetError}
                            </div>
                        )}
                        {resetSuccess && (
                            <div
                                style={{
                                    background: 'rgba(76, 175, 80, 0.15)',
                                    color: '#2e7d32',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    marginBottom: '12px',
                                    fontSize: '12px',
                                }}
                            >
                                {resetSuccess}
                            </div>
                        )}
                        {resetStep === 'request' ? (
                            <form onSubmit={handleResetRequest}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                        }}
                                    >
                                        メールアドレス
                                    </label>
                                    <input
                                        type='email'
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                        }}
                                        placeholder='登録済みのメールアドレスを入力'
                                    />
                                </div>
                                <button
                                    type='submit'
                                    disabled={resetLoading}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        background: resetLoading ? '#ccc' : '#667eea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        cursor: resetLoading ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {resetLoading ? '送信中...' : '認証コードを送信'}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleResetConfirm}>
                                <div style={{ marginBottom: '12px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                        }}
                                    >
                                        認証コード
                                    </label>
                                    <input
                                        type='text'
                                        value={resetCode}
                                        onChange={(e) => setResetCode(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                            letterSpacing: '2px',
                                        }}
                                        placeholder='6桁の認証コード'
                                    />
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                        }}
                                    >
                                        新しいパスワード
                                    </label>
                                    <input
                                        type='password'
                                        value={resetNewPassword}
                                        onChange={(e) => setResetNewPassword(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                        }}
                                        placeholder='新しいパスワード'
                                        autoComplete='new-password'
                                    />
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                        }}
                                    >
                                        新しいパスワード（確認用）
                                    </label>
                                    <input
                                        type='password'
                                        value={resetConfirmPassword}
                                        onChange={(e) => setResetConfirmPassword(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box',
                                        }}
                                        placeholder='新しいパスワードを再入力'
                                        autoComplete='new-password'
                                    />
                                </div>
                                <button
                                    type='submit'
                                    disabled={resetLoading}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        background: resetLoading ? '#ccc' : '#667eea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        cursor: resetLoading ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {resetLoading ? '更新中...' : 'パスワードを更新'}
                                </button>
                            </form>
                        )}
                        <button
                            type='button'
                            onClick={closeResetModal}
                            style={{
                                width: '100%',
                                marginTop: '15px',
                                padding: '10px',
                                background: 'rgba(0, 0, 0, 0.05)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 'bold',
                            }}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default LoginScreen;
